// ─── RENDER QUALITY ──────────────────────────────────────────────────────────
//
// A user-facing quality setting for the 3D views, on top of the automatic
// device detection in gpuProfile.
//
// The automatic choice is deliberately conservative on phones, because the
// alternative was mobile browsers running out of GPU memory and killing the tab
// (which looks like the page reloading itself). But "phone" covers everything
// from an old budget handset to a current flagship, and someone on a fast device
// may well prefer the nicer picture. This lets them say so — and lets someone on
// a struggling device turn things down further.
//
// AUTO IS THE DEFAULT AND CHANGES NOTHING. It defers entirely to gpuProfile, so
// anyone who never touches this setting gets exactly the behaviour they had
// before it existed. Only an explicit choice overrides anything.

import { useSyncExternalStore } from 'react';
import { isLowPowerGPU, shadowMapSize, postFxSamples } from './gpuProfile';

export const QUALITY_LEVELS = ['auto', 'low', 'medium', 'high'];
const STORAGE_KEY = 'tunify.renderQuality';

function readStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (QUALITY_LEVELS.includes(v)) return v;   // an explicit choice always wins
  } catch {
    /* private mode / storage disabled — fall through to the default */
  }
  return defaultLevel();
}

// PHONES AND TABLETS START ON LOW.
//
// "Auto" still renders at up to 2× pixel ratio with shadows and a full-size
// shadow map, and phones were still having the tab killed — the page appearing
// to reload itself mid-session. Low drops the pixel ratio to 1 (a quarter of the
// pixels to shade and store), turns off shadow rendering entirely and shrinks
// the shadow map to 512, which is by far the biggest reduction available.
//
// This is only the STARTING point. Anyone who picks a level keeps it — the
// choice is stored — so someone on a capable phone can move up to high and stay
// there. Desktops are unaffected and still start on auto.
function defaultLevel() {
  return isLowPowerGPU() ? 'low' : 'auto';
}

let current = readStored();
const listeners = new Set();

export function getQuality() {
  return current;
}

export function setQuality(level) {
  if (!QUALITY_LEVELS.includes(level) || level === current) return;
  current = level;
  try { localStorage.setItem(STORAGE_KEY, level); } catch { /* not fatal */ }
  listeners.forEach((fn) => fn());
}

/** Current quality level, re-rendering the component when it changes. */
export function useQuality() {
  return useSyncExternalStore(
    (fn) => { listeners.add(fn); return () => listeners.delete(fn); },
    () => current,
    () => 'auto',   // server render
  );
}

/**
 * The actual render settings for a level.
 *
 * Every one of these can be changed on a LIVE canvas — device pixel ratio, the
 * post-processing sample count, whether the sun casts shadows, and the shadow
 * map's resolution (via a React key on the light). Nothing here forces the
 * Canvas to remount, so switching quality does not reset the camera or reload
 * the room.
 */
export function qualitySettings(level = current) {
  switch (level) {
    // Cheapest that still looks like a room: no shadow work at all, native
    // resolution only, no post-processing buffer.
    case 'low':
      return { dpr: 1, shadows: false, shadowMap: 512, samples: 0, label: 'low' };

    case 'medium':
      return { dpr: [1, 1.5], shadows: true, shadowMap: 1024, samples: 0, label: 'medium' };

    // Everything on. On a phone this is the combination that was crashing
    // tabs, which is why the warning next to the control is not decorative.
    case 'high':
      return {
        dpr: [1, 2],
        shadows: true,
        shadowMap: isLowPowerGPU() ? 2048 : 4096,
        samples: 8,
        label: 'high',
      };

    // Auto — defer completely to the device detection.
    default:
      return {
        dpr: [1, 2],
        shadows: true,
        shadowMap: shadowMapSize(),
        samples: postFxSamples(),
        label: 'auto',
      };
  }
}
